/**
 * Hook for enabling/disabling the Flywheel for an organization.
 *
 * One call does it: POST /api/v1/organizations/{id}/flywheel with
 * `{ enabled }`. The server owns everything enabling implies, so the client
 * states the intent and nothing else — no settings writes, no flow
 * provisioning, no transfer numbers or business hours.
 */

import { useApolloClient } from '@apollo/client/react';
import { useMutation } from '@tanstack/react-query';
import { setOrganizationFlywheel } from '../../api/remiApi';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { mergeFlywheelSetting } from './mergeFlywheelSetting';

interface SaveFlywheelConfigParams {
  organizationId: string;
  flywheelEnabled: boolean;
}

/**
 * Enable or disable the Flywheel for an organization.
 */
export const useSaveFlywheelConfig = () => {
  const apolloClient = useApolloClient();

  return useMutation({
    mutationFn: async ({ organizationId, flywheelEnabled }: SaveFlywheelConfigParams) => {
      await setOrganizationFlywheel(organizationId, flywheelEnabled);
      return { success: true, organizationId, flywheelEnabled };
    },
    onSuccess: async ({ organizationId, flywheelEnabled }) => {
      // `settings` is a JSON *string* on the wire, and both the single-org and
      // the org-list queries read it off the same normalized entity. Merging
      // has to parse and re-stringify: spreading the string writes an object
      // ({0:'{',1:'"'...}), which the list then reads raw and the detail query
      // silently drops when JSON.parse throws.
      apolloClient.cache.modify<{ settings: string | null }>({
        id: apolloClient.cache.identify({ __typename: 'Organization', organizationId }),
        fields: {
          settings: (existing) => mergeFlywheelSetting(existing, flywheelEnabled),
        },
      });

      // Client Admin reads Apollo, but every gated surface (handoff bell,
      // widgets, SMS) reads the entitlement from the org store, which no
      // refetch here touches. Without this the toggle only takes effect on the
      // next full reload — and only for the signed-in user's own org.
      const store = useOrganizationStore.getState();
      if (store.organization?.id === organizationId) {
        await store.fetchOrganization(true);
      }
    },
  });
};
